import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminConfigGet, adminConfigPatch } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save } from "lucide-react";
import { Section, Field } from "./_layout";

export default function ConfigTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({ queryKey: ["admin-config"], queryFn: adminConfigGet });
  const [form, setForm] = useState(null);
  useEffect(() => { if (data && !form) setForm(data); }, [data, form]);

  const patchMut = useMutation({
    mutationFn: (body) => adminConfigPatch(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-config"] });
      qc.invalidateQueries({ queryKey: ["admin-market-budget"] });
      toast({ title: "Config saved" });
    },
  });
  if (isLoading || !form) return <Loader2 className="size-5 animate-spin text-white/50" />;
  const upd = (k, v) => setForm({ ...form, [k]: v });

  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="admin-config-form">
      <Section title="USDT Wallet addresses (where users send USDT)">
        <Field label="TRC-20 (Tron) address" testid="admin-trc20">
          <input type="text" value={form.usdt_trc20_address || ""} onChange={(e) => upd("usdt_trc20_address", e.target.value)}
            placeholder="T…"
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm font-mono" />
        </Field>
        <Field label="BEP-20 (BSC) address" testid="admin-bep20">
          <input type="text" value={form.usdt_bep20_address || ""} onChange={(e) => upd("usdt_bep20_address", e.target.value)}
            placeholder="0x…"
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm font-mono" />
        </Field>
        <Field label="TronGrid API key (optional)" testid="admin-tron-key">
          <input type="text" value={form.tron_api_key || ""} onChange={(e) => upd("tron_api_key", e.target.value)}
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm font-mono" />
        </Field>
        <Field label="BscScan API key (recommended)" testid="admin-bsc-key">
          <input type="text" value={form.bsc_api_key || ""} onChange={(e) => upd("bsc_api_key", e.target.value)}
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm font-mono" />
        </Field>
      </Section>

      <Section title="Trial & Market polling">
        <Field label="Default trial (days)" testid="admin-trial-default">
          <input type="number" value={form.trial_default_days ?? 7} onChange={(e) => upd("trial_default_days", Number(e.target.value))}
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm" />
        </Field>
        <Field label="TwelveData poll interval" testid="admin-td-interval">
          <select value={form.td_poll_interval_minutes ?? 1} onChange={(e) => upd("td_poll_interval_minutes", Number(e.target.value))}
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm">
            <option value={1}>1 minute (real-time, heaviest)</option>
            <option value={5}>5 minutes (recommended)</option>
            <option value={15}>15 minutes</option>
            <option value={30}>30 minutes</option>
            <option value={60}>60 minutes (lightest)</option>
          </select>
        </Field>
        <Field label="Auto-throttle when credits low" testid="admin-td-throttle">
          <select value={String(form.td_auto_throttle_enabled ?? true)} onChange={(e) => upd("td_auto_throttle_enabled", e.target.value === "true")}
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm">
            <option value="true">Enabled — switch to 5m when low</option>
            <option value="false">Disabled (keep manual interval)</option>
          </select>
        </Field>
        <Field label="Low-credit threshold (%)" testid="admin-td-threshold">
          <input type="number" value={form.td_low_threshold_pct ?? 20} onChange={(e) => upd("td_low_threshold_pct", Number(e.target.value))}
            className="w-full bg-ink-800 border border-white/10 rounded px-2 py-1.5 text-sm" />
        </Field>
      </Section>

      <button
        onClick={() => patchMut.mutate(form)}
        data-testid="admin-config-save-btn"
        className="lg:col-span-2 bg-gold-grad text-ink-900 px-4 py-2 rounded-lg font-semibold text-sm inline-flex items-center justify-center gap-1"
      >
        {patchMut.isPending ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
        Save all
      </button>
    </div>
  );
}
